import { useEffect, useMemo, useRef, useState } from "react";
import {
  UserNotificationDto,
  useMarkNotificationsRead,
  useUnreadNotificationsCount,
  useUserNotifications,
} from "../../../api/notifications-api";
import { useGetSiteApi } from "../../../api/site-api";
import Dropdown from "../../Dropdown/Dropdown";
import Icon from "../../Icon";
import Tooltip from "../../Tooltip";
import styles from "./NotificationDropdown.module.css";
import NotificationSettings from "./NotificationSettings";

type NotificationDropdownProps = {
  unreadCount?: number;
  site: string;
};

type NotificationItem = {
  key: string;
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  timestamp: string;
  instance: string;
  unread: boolean;
  chartUrl: string | null;
  equipmentPath: string;
  acknowledgedLabel: string | null;
  acknowledgedBy: string | null;
  acknowledgedComment: string | null;
  mutedLabel: string | null;
};

function mapNotificationDtoToItem(dto: UserNotificationDto): NotificationItem {
  const id = dto.AlarmId;
  const title = dto.TypeString || "Notification";
  const subtitle = dto.Description || dto.Message || dto.Instance || "";
  const timestampRaw = dto.Timestamp;
  return {
    key: "",
    id,
    icon: "Alarm_bell",
    title,
    subtitle,
    timestamp: formatNotificationTimestamp(timestampRaw),
    instance: dto.Instance || "",
    unread: !dto.Read,
    chartUrl: dto.ChartUrl || null,
    equipmentPath: dto.EquipmentPath || "",
    acknowledgedLabel: dto.Acknowledged
      ? `Acknowledged${
          dto.AcknowledgedAt
            ? ` ${formatNotificationTimestamp(dto.AcknowledgedAt)}`
            : ""
        }`
      : null,
    acknowledgedBy: dto.AcknowledgedBy || null,
    acknowledgedComment: findAcknowledgementComment(dto),
    // A muted alarm without an end date is muted until it is explicitly unmuted.
    mutedLabel: dto.Muted ? formatMutedUntil(dto.MutedUntil) : null,
  };
}

function mapNotificationDtosToItems(
  notifications: UserNotificationDto[] = []
): NotificationItem[] {
  const keyOccurrences = new Map<string, number>();

  return notifications.map((notification) => {
    const item = mapNotificationDtoToItem(notification);
    const keyBase = [
      item.id,
      item.instance,
      notification.Timestamp || "",
      item.title,
      item.subtitle,
    ].join("::");
    const occurrence = keyOccurrences.get(keyBase) ?? 0;

    keyOccurrences.set(keyBase, occurrence + 1);

    return {
      ...item,
      key: `${keyBase}::${occurrence}`,
    };
  });
}

function formatNotificationTimestamp(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  const hh = `${date.getHours()}`.padStart(2, "0");
  const min = `${date.getMinutes()}`.padStart(2, "0");
  return `${yyyy}-${mm}-${dd}, ${hh}:${min}`;
}

// The API appends an acknowledgement's comment to the same thread as any other comment,
// with no marker distinguishing it, so it has to be inferred: the most recent comment by
// whoever acknowledged. Comments arrive ordered by timestamp ascending.
//
// Deliberately not matched on time. The comment's timestamp is serialized without a zone
// (DateTime.Parse yields Unspecified) while AcknowledgedAt carries one, so JS reads the
// first as local and the second as absolute - hours apart for any non-UTC viewer.
function findAcknowledgementComment(dto: UserNotificationDto): string | null {
  if (!dto.Acknowledged || !dto.AcknowledgedBy) return null;

  const byAcknowledger = (dto.Comments ?? []).filter(
    (comment) => comment.UserId === dto.AcknowledgedBy && comment.Comment
  );

  return byAcknowledger[byAcknowledger.length - 1]?.Comment ?? null;
}

// Same three cases as the mobile badge (MutedUntilConverter): the end time relative to
// today, with a full date only once it is further out. The date itself keeps this surface's
// yyyy-MM-dd convention rather than mobile's yyyy/MM/dd, to match the column beside it.
// Null content makes Tooltip render the trigger alone, so a notification with no
// acknowledger and no comment simply has no hover card.
function renderAcknowledgementDetails(item: NotificationItem) {
  if (!item.acknowledgedBy && !item.acknowledgedComment) return null;

  return (
    <span className={styles.ackDetails}>
      {item.acknowledgedComment && (
        <>
          <span className={styles.ackDetailsLabel}>Comment</span>
          <span className={styles.ackDetailsValue}>
            {item.acknowledgedComment}
          </span>
        </>
      )}
      {item.acknowledgedBy && (
        <>
          <span className={styles.ackDetailsLabel}>By</span>
          <span className={styles.ackDetailsValue}>{item.acknowledgedBy}</span>
        </>
      )}
    </span>
  );
}

function formatMutedUntil(value: string | null): string {
  if (!value) return "Muted";

  const until = new Date(value);
  if (Number.isNaN(until.getTime())) return "Muted";

  const time = `${`${until.getHours()}`.padStart(2, "0")}:${`${until.getMinutes()}`.padStart(2, "0")}`;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfUntil = new Date(until);
  startOfUntil.setHours(0, 0, 0, 0);
  const days = Math.round(
    (startOfUntil.getTime() - startOfToday.getTime()) / 86_400_000
  );

  if (days <= 0) return `Until ${time}`;
  if (days === 1) return `Until tomorrow, ${time}`;
  return `Until ${formatNotificationDate(value)}, ${time}`;
}

function formatNotificationDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getRootPath(instance: string, equipmentPath: string): string {
  const [customer, motor = ""] = instance.split(".");
  const area = equipmentPath.split("/")[1] ?? "";
  return `${customer}/${area}/${motor}`;
}

function NotificationDropdown({
  unreadCount = 0,
  site,
}: NotificationDropdownProps) {
  const { data: siteData } = useGetSiteApi(site);
  const [isOpen, setIsOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All mills");
  const millTabs = useMemo(
    () => [
      "All mills",
      ...new Set((siteData?.rootEquipments ?? []).filter(Boolean)),
    ],
    [siteData?.rootEquipments]
  );
  const unreadCountQuery = useUnreadNotificationsCount(site);
  const notificationsQuery = useUserNotifications(site, isOpen);
  const markNotificationsReadMutation = useMarkNotificationsRead(site);
  const hoverTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const readRequestPending = useRef<Set<string>>(new Set());
  const suppressQueryHydrationRef = useRef(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isRefreshingNotifications, setIsRefreshingNotifications] =
    useState(false);

  useEffect(() => {
    return () => {
      Object.values(hoverTimers.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
    };
  }, []);

  useEffect(() => {
    if (!notificationsQuery.data) {
      return;
    }

    if (suppressQueryHydrationRef.current) {
      return;
    }

    setNotifications(mapNotificationDtosToItems(notificationsQuery.data));
  }, [notificationsQuery.data]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isCancelled = false;
    suppressQueryHydrationRef.current = true;
    setIsRefreshingNotifications(true);
    setNotifications([]);

    void (async () => {
      try {
        const [notificationsResult] = await Promise.all([
          notificationsQuery.refetch(),
          unreadCountQuery.refetch(),
        ]);

        if (isCancelled) {
          return;
        }

        setNotifications(mapNotificationDtosToItems(notificationsResult.data));
      } finally {
        if (!isCancelled) {
          suppressQueryHydrationRef.current = false;
          setIsRefreshingNotifications(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, notificationsQuery.refetch, unreadCountQuery.refetch]);

  useEffect(() => {
    if (!millTabs.includes(activeTab)) {
      setActiveTab("All mills");
    }
  }, [activeTab, millTabs]);

  const clearHoverTimer = (id: string) => {
    const timer = hoverTimers.current[id];
    if (timer) {
      clearTimeout(timer);
      delete hoverTimers.current[id];
    }
  };

  const clearAllHoverTimers = () => {
    Object.keys(hoverTimers.current).forEach(clearHoverTimer);
  };

  const handleNotificationHoverStart = (id: string, unread: boolean) => {
    if (
      !unread ||
      hoverTimers.current[id] ||
      readRequestPending.current.has(id)
    ) {
      return;
    }

    hoverTimers.current[id] = setTimeout(() => {
      setNotifications((current) =>
        current.map((item) =>
          item.id === id && item.unread ? { ...item, unread: false } : item
        )
      );

      readRequestPending.current.add(id);
      markNotificationsReadMutation.mutate([id], {
        onError: () => {
          setNotifications((current) =>
            current.map((item) =>
              item.id === id ? { ...item, unread: true } : item
            )
          );
        },
        onSettled: () => {
          readRequestPending.current.delete(id);
        },
      });

      delete hoverTimers.current[id];
    }, 300);
  };

  const handleNotificationHoverEnd = (id: string) => {
    clearHoverTimer(id);
  };

  const handleOpenSettingsModal = () => {
    setIsOpen(false);
    setIsSettingsModalOpen(true);
  };

  const handleMarkAllAsRead = () => {
    const unreadNotificationIds = Array.from(
      new Set(
        notifications
          .filter((item) => item.unread)
          .map((item) => item.id)
          .filter(Boolean)
      )
    );

    if (unreadNotificationIds.length === 0) {
      return;
    }

    clearAllHoverTimers();
    unreadNotificationIds.forEach((id) => readRequestPending.current.add(id));
    setNotifications((current) =>
      current.map((item) => (item.unread ? { ...item, unread: false } : item))
    );

    markNotificationsReadMutation.mutate(unreadNotificationIds, {
      onError: () => {
        setNotifications((current) =>
          current.map((item) =>
            unreadNotificationIds.includes(item.id)
              ? { ...item, unread: true }
              : item
          )
        );
      },
      onSettled: () => {
        unreadNotificationIds.forEach((id) =>
          readRequestPending.current.delete(id)
        );
        void unreadCountQuery.refetch();
      },
    });
  };

  const handleCloseSettingsModal = () => {
    setIsSettingsModalOpen(false);
    void unreadCountQuery.refetch();
  };

  const openChart = (item: NotificationItem) => {
    const rootPath = encodeURIComponent(
      getRootPath(item.instance, item.equipmentPath)
    );
    const dashboardRoute = `/report/__dynamic__?rootPath=${rootPath}&${item.chartUrl}`;
    const params = new URLSearchParams({ dashboardRoute });
    window.open(
      `${window.location.origin}/condition-monitoring/dashboards/site/${site}?${params.toString()}`,
      "_blank"
    );
  };

  const unreadItemsCount = unreadCountQuery.data ?? unreadCount;
  const badgeText = unreadItemsCount > 99 ? "99+" : `${unreadItemsCount}`;
  const hasUnread = unreadItemsCount > 0;

  const visibleNotifications =
    activeTab === "All mills"
      ? notifications
      : notifications.filter(
          (item) => item.instance === `${site}.${activeTab}`
        );
  const hasUnreadNotifications = notifications.some((item) => item.unread);

  const content = (
    <div className={styles.content}>
      <div className={styles.header}>
        <h3>Notifications</h3>
        {hasUnread && (
          <span className={styles.newPill}>{`${unreadItemsCount} new`}</span>
        )}
      </div>

      <div className={styles.tabs}>
        {millTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            className={
              styles.tab + (activeTab === tab ? ` ${styles.activeTab}` : "")
            }
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className={styles.list}>
        {isRefreshingNotifications || notificationsQuery.isLoading ? (
          <div className={styles.listState}>Loading notifications...</div>
        ) : notificationsQuery.isError ? (
          <div className={styles.listState}>Could not load notifications.</div>
        ) : visibleNotifications.length === 0 ? (
          <div className={styles.listState}>No notifications.</div>
        ) : (
          visibleNotifications.map((item) => (
            <button
              className={styles.item}
              key={item.key}
              type="button"
              onMouseEnter={() =>
                handleNotificationHoverStart(item.id, item.unread)
              }
              onMouseLeave={() => handleNotificationHoverEnd(item.id)}
            >
              <div className={styles.itemLeft}>
                <span
                  className={
                    styles.unreadDot +
                    (item.unread ? "" : ` ${styles.hiddenUnreadDot}`)
                  }
                ></span>
                <Icon
                  name={item.icon}
                  className={styles.itemIcon}
                  size="1.125rem"
                ></Icon>
                <div className={styles.itemText}>
                  <p className={styles.itemTitle}>{item.title}</p>
                  <p className={styles.itemSubtitle}>{item.subtitle}</p>
                  {(item.acknowledgedLabel || item.mutedLabel) && (
                    <p className={styles.itemStatus}>
                      {item.acknowledgedLabel && (
                        <Tooltip
                          placement="bottom"
                          content={renderAcknowledgementDetails(item)}
                        >
                          <span className={styles.statusAcknowledged}>
                            <Icon name="Check_mark" size="0.875rem"></Icon>
                            {item.acknowledgedLabel}
                          </span>
                        </Tooltip>
                      )}
                      {item.mutedLabel && (
                        <span className={styles.statusMuted}>
                          <Icon name="Audio_off" size="0.875rem"></Icon>
                          {item.mutedLabel}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <p className={styles.itemTime}>{item.timestamp}</p>
              {item.chartUrl && (
                <span
                  className={styles.chartButton}
                  title="View chart"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    openChart(item);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                      openChart(item);
                    }
                  }}
                >
                  <Icon name="Bar_chart" size="1.125rem"></Icon>
                </span>
              )}
            </button>
          ))
        )}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.settingsButton}
          type="button"
          onClick={handleOpenSettingsModal}
        >
          <Icon name="Settings" size="1rem"></Icon>
          Notification settings
        </button>
        <button
          className={styles.markAllButton}
          type="button"
          onClick={handleMarkAllAsRead}
          disabled={
            !hasUnreadNotifications || markNotificationsReadMutation.isPending
          }
        >
          Mark all as read
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Dropdown
        anchor="right"
        dropdown={content}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      >
        <div
          className={
            styles["notification-icon-container"] +
            (hasUnread ? ` ${styles.hasUnread}` : "") +
            (isOpen ? ` ${styles.open}` : "")
          }
          onClick={() => setIsOpen((o) => !o)}
          aria-label="Notifications"
        >
          <Icon name="Alarm_bell" size="1rem"></Icon>
          {hasUnread && <span className={styles.badge}>{badgeText}</span>}
        </div>
      </Dropdown>
      <NotificationSettings
        customer={site}
        isOpen={isSettingsModalOpen}
        onClose={handleCloseSettingsModal}
      />
    </>
  );
}

export default NotificationDropdown;
